import { RefObject, useEffect } from 'react';
import gsap from 'gsap';
import { Observer } from 'gsap/Observer';

gsap.registerPlugin(Observer);

interface UseSheetGesturesOptions {
  onClose: () => void;
  isOpen: boolean;
}

export const useSheetGestures = (
  sheetRef: RefObject<HTMLDivElement>,
  handleRef: RefObject<HTMLDivElement>,
  options: UseSheetGesturesOptions
) => {
  const { onClose, isOpen } = options;

  useEffect(() => {
    if (!sheetRef.current || !handleRef.current || !isOpen) return undefined;

    const sheet = sheetRef.current;
    const handle = handleRef.current;
    let startY = 0;
    let isDragging = false;

    const ctx = gsap.context(() => {
      Observer.create({
        target: handle,
        type: 'touch,pointer',
        onPress: (self) => {
          isDragging = true;
          startY = self.y ?? 0;
        },
        onDrag: (self) => {
          if (!isDragging) return;
          
          const deltaY = (self.y ?? 0) - startY;
          
          // Solo permitir arrastrar hacia abajo
          if (deltaY > 0) {
            gsap.set(sheet, { y: deltaY });
          }
        },
        onRelease: (self) => {
          if (!isDragging) return;
          isDragging = false;
          
          const deltaY = (self.y ?? 0) - startY;
          const velocity = self.velocityY ?? 0;
          
          // Si se arrastró más de 100px o la velocidad es alta, cerrar
          if (deltaY > 100 || velocity > 0.5) {
            gsap.to(sheet, {
              y: '100%',
              duration: 0.3,
              ease: 'power2.in',
              onComplete: onClose,
            });
          } else {
            // Volver a la posición original
            gsap.to(sheet, {
              y: 0,
              duration: 0.3,
              ease: 'power2.out',
            });
          }
        },
        tolerance: 10,
        preventDefault: true,
      });
    });

    return () => {
      void ctx.revert();
    };
  }, [onClose, isOpen]);
};
